import { ReactNode, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HardHat,
  FolderKanban,
  Sun,
  Menu,
  LayoutGrid,
  LogOut,
} from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { isOperatorExperience } from '@/auth/permissions';
import { navWorkspacesFor, visibleWorkspaces, workspaceForPath, workspaceHome } from './navWorkspaces';
import { SyncStatusBadge } from '@/offline/SyncStatusBadge';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { NAV_BADGE_LABELS, useNavBadges } from '@/app/hooks/useNavBadges';
import { cn } from '@/lib/utils';
import { ShellSlotsContext } from '@/lib/shellSlots';
import { WorkspaceSheet } from './WorkspaceSheet';
import { AreaTabs } from './AreaTabs';
import { NadcAppsLauncher } from './NadcAppsLauncher';

interface MobileLayoutProps {
  children: ReactNode;
}

// Bottom nav limited to 5 items max. Today replaces Messages (a Phase-5
// placeholder page) — field users get the landing screen, Messages stays
// reachable from More until it actually exists. `badges` names counts from
// useNavBadges(): the equipment inbox and the Sync Center both live under
// More on a phone, so their counts sit on the More tab, summed — the only
// cue a phone had was the bell.
type BottomNavItem = { to: string; icon: typeof Menu; label: string; badges?: string[]; active: (pathname: string) => boolean };
// 2026-09-25: Today holds what needs you, so Home went; Field takes the slot —
// the crews are who carry a phone. Since the audit (2026-10-14) Projects and
// Field come from the menu file: a tab shows only when its workspace has a page
// this person can open, and it goes where the desk's row goes (workspaceHome) —
// a fixed /schedule tab sent everyone without the Field Board to a refusal.
const TODAY_PATHS = ['/', '/today', '/action-center'];
function fullBottomNavItems(can: (p: string) => boolean): BottomNavItem[] {
  const visible = visibleWorkspaces(can);
  const area = (key: string, icon: typeof Menu): BottomNavItem[] => {
    const ws = visible.find((w) => w.key === key);
    const to = ws ? workspaceHome(ws, can) : null;
    return ws && to ? [{ to, icon, label: ws.label, active: (path) => workspaceForPath(path)?.key === key }] : [];
  };
  return [
    { to: '/today', icon: Sun, label: 'Today', active: (path) => TODAY_PATHS.includes(path) },
    ...area('projects', FolderKanban),
    ...area('field', HardHat),
    { to: '/menu', icon: Menu, label: 'More', badges: ['equipment_inbox', 'purchasing_inbox', 'unsent'], active: (path) => path.startsWith('/menu') },
  ];
}

/**
 * Operator experience (2026-09-17, spec §2.3): an operator's bottom nav is
 * the operator configuration's entries — My job — plus a sign-out tab, and
 * the Workspaces button goes too (one workspace needs no chooser). Read from
 * navWorkspacesFor() so the phone and the desktop rail cannot disagree.
 */
const operatorBottomNavItems: BottomNavItem[] = navWorkspacesFor('operator')
  .flatMap((w) => w.items)
  .map((i) => ({ to: i.to, icon: i.icon, label: i.label, active: (path: string) => path.startsWith(i.to) }));

export function MobileLayout({ children }: MobileLayoutProps) {
  const location = useLocation();
  const badges = useNavBadges();
  const { user, logout, can } = useAuth();
  const operator = isOperatorExperience(user);
  const bottomNavItems = useMemo(() => (operator ? operatorBottomNavItems : fullBottomNavItems(can)), [operator, can]);
  const [workspacesOpen, setWorkspacesOpen] = useState(false);
  // No dock on a phone: the header is how you get around, and nothing new
  // goes into it. The subbar is the slot under it (lib/shellSlots.tsx).
  const [subbar, setSubbar] = useState<HTMLDivElement | null>(null);
  const slots = useMemo(() => ({ dock: null, subbar }), [subbar]);

  return (
    <div className="flex flex-col h-screen">
      {/* Top header — `relative` so the bell's and the NADC apps button's
          compact panels anchor under it, and marked so the Guide starts
          below it. 48px since 2026-10-10 (compact chrome, Michael: "it uses
          too much space"), and min-h rather than h: safe-top pads it under a
          notch, and must grow it rather than squash it. */}
      <header
        data-guide-chrome="top-bar"
        className="relative flex min-h-12 items-center justify-between border-b border-graphite-800 bg-graphite-900 px-4 safe-top"
      >
        <div className="flex items-center gap-3">
          {/* "Return to Portal" went 2026-09-08, when the portal bar's user
              menu was the launcher. The bar itself went 2026-10-09 (Michael,
              to save screen space): the NADC apps button on the right is the
              launcher now. */}
          <span className="text-base font-bold text-safety-500">DemoOps</span>
        </div>
        <div className="flex items-center gap-1">
          {/* The rail's phone equivalent — see WorkspaceSheet. The bottom nav
              and /menu are untouched; this sits beside them. */}
          {!operator && (
            <button
              type="button"
              onClick={() => setWorkspacesOpen(true)}
              aria-label="Workspaces"
              className="flex h-9 w-9 items-center justify-center rounded-md text-graphite-400 transition-colors hover:bg-graphite-800 hover:text-graphite-100"
            >
              <LayoutGrid className="h-5 w-5" aria-hidden />
            </button>
          )}
          <NotificationBell compact />
          {/* The NADC apps (2026-10-09): what the shared bar's user menu was —
              other NADC apps, My Tickets, Get Help, Sign out. Everyone has
              it, operators too: everyone had the bar. */}
          <NadcAppsLauncher compact />
          {/* An operator's nav is My job alone (spec §2.3): the badge still
              shows the queue, the Sync Center link does not. */}
          {operator ? <SyncStatusBadge compact /> : <Link to="/sync" title="Sync Center"><SyncStatusBadge compact /></Link>}
        </div>
        {/* Under the header, clear of its bottom border: the project page's
            sticky bar. */}
        <div ref={setSubbar} data-testid="shell-subbar" className="absolute inset-x-0 top-full z-40 mt-px" />
      </header>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {/* The area's tab strip (2026-09-25). It stays in the page on a phone
            and scrolls away with it (2026-10-10): the header already holds
            five controls at 360px, and a fixed second row would cost 40px on
            every screen. The desktop's is its top row. */}
        <AreaTabs variant="strip" />
        <ShellSlotsContext.Provider value={slots}>{children}</ShellSlotsContext.Provider>
      </main>

      {/* Bottom navigation. data-guide-chrome lets the Guide measure it and sit
          above it rather than covering it — see usePanelPlacement. */}
      <nav data-guide-chrome="bottom-nav" className="bg-graphite-900 border-t border-graphite-800 safe-bottom">
        <ul className="flex justify-around">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.active(location.pathname);
            const parts = (item.badges ?? []).map((b) => [b, badges[b] ?? 0] as const).filter(([, n]) => n > 0);
            const count = parts.reduce((sum, [, n]) => sum + n, 0);
            const badgeLabel = parts.map(([b, n]) => NAV_BADGE_LABELS[b]?.(n) ?? `${n} waiting`).join(', ');

            return (
              <li key={item.to} className="flex-1">
                <Link
                  to={item.to}
                  className={cn(
                    'flex flex-col items-center py-2 px-1',
                    'transition-colors min-h-touch',
                    isActive
                      ? 'text-safety-500'
                      : 'text-graphite-500'
                  )}
                >
                  <span className="relative">
                    <Icon className="w-6 h-6" />
                    {count > 0 && (
                      <span
                        aria-label={badgeLabel}
                        className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] px-1 rounded-full bg-safety-500 text-white text-[11px] font-bold leading-[18px] text-center tabular-nums"
                      >
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                  </span>
                  <span className="text-xs mt-1">{item.label}</span>
                </Link>
              </li>
            );
          })}
          {operator && (
            <li className="flex-1">
              <button
                type="button"
                onClick={() => logout()}
                className="flex w-full flex-col items-center px-1 py-2 text-graphite-500 transition-colors min-h-touch"
              >
                <LogOut className="w-6 h-6" aria-hidden />
                <span className="text-xs mt-1">Sign out</span>
              </button>
            </li>
          )}
        </ul>
      </nav>

      {workspacesOpen && <WorkspaceSheet onClose={() => setWorkspacesOpen(false)} />}
    </div>
  );
}
